import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Form, Spinner, Table } from 'react-bootstrap';
import moment from 'moment';
import { AuthAPI } from '../../API';

/**
 * ChangeLogPage — global audit-trail browser for admins / PMs / sub-admins.
 *
 * Filters at the top let the user narrow by entity type, action, and a
 * from/to date range. The backend (Controllers/ChangeLog.controller.js)
 * already supports `entityType`, `action`, `fromDate`, `toDate`, `limit`,
 * `offset` so the UI just forwards the values verbatim.
 *
 * Each row is expandable — clicking "View" opens a panel showing the full
 * JSONB `changes` object with old -> new for every field that moved. The
 * collapsed row shows a compact summary string assembled by the backend.
 */

type ChangeLogRow = {
  id: string;
  entityType: string;
  entityId: string;
  entityName: string | null;
  action: string;
  changedBy: string | null;
  changedByName: string | null;
  changes: Record<string, { old: any; new: any }> | null;
  summary: string | null;
  createdAt: string;
  updatedAt: string;
};

type Filters = {
  entityType: string;
  action: string;
  fromDate: string;
  toDate: string;
  limit: number;
  offset: number;
};

const DEFAULT_FILTERS: Filters = {
  entityType: '',
  action: '',
  fromDate: '',
  toDate: '',
  limit: 100,
  offset: 0,
};

const ACTION_VARIANT: Record<string, string> = {
  create: 'success',
  update: 'info',
  status_change: 'warning',
  delete: 'danger',
};

const actionBadge = (action: string) => (
  <Badge variant={ACTION_VARIANT[action] || 'secondary'}>{action}</Badge>
);

const formatValue = (v: any): string => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const ChangeLogPage: React.FC = () => {
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [rows, setRows] = useState<ChangeLogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const load = async (next: Filters) => {
    setLoading(true);
    setError(null);
    try {
      // Only send non-empty filters so backend defaults apply
      const payload: Record<string, any> = { limit: next.limit, offset: next.offset };
      if (next.entityType) payload.entityType = next.entityType;
      if (next.action) payload.action = next.action;
      if (next.fromDate) payload.fromDate = next.fromDate;
      if (next.toDate) payload.toDate = next.toDate;
      const res = await AuthAPI.changeLogsList(payload);
      setRows(res.rows || []);
      setTotal(res.total || 0);
    } catch (e: any) {
      setError((e && e.message) || 'Failed to load change logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(filters);
    // intentionally load once on mount; subsequent loads are triggered by the Search button
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const hasNext = useMemo(() => filters.offset + (rows?.length || 0) < total, [filters.offset, rows, total]);
  const hasPrev = filters.offset > 0;

  const handleSearch = () => {
    const next = { ...filters, offset: 0 };
    setFilters(next);
    load(next);
  };

  const handlePage = (delta: number) => {
    const next = { ...filters, offset: Math.max(0, filters.offset + delta * filters.limit) };
    setFilters(next);
    load(next);
  };

  return (
    <div className="p-3">
      <h4 className="mb-3">Change Log</h4>
      <p className="text-muted small mb-3">
        Audit trail of who changed what. Includes surveys created, updated, status-flipped, and soft-deleted.
      </p>

      {/* Filters */}
      <Form>
        <div className="row">
          <Form.Group className="col-md-2">
            <Form.Label>Entity Type</Form.Label>
            <Form.Control
              as="select"
              value={filters.entityType}
              onChange={(e) => setFilters({ ...filters, entityType: e.target.value })}
            >
              <option value="">All</option>
              <option value="survey">Survey</option>
              <option value="sample">Sample</option>
              <option value="partner">Partner</option>
              <option value="emailSchedule">Email Schedule</option>
              <option value="user">User</option>
            </Form.Control>
          </Form.Group>
          <Form.Group className="col-md-2">
            <Form.Label>Action</Form.Label>
            <Form.Control
              as="select"
              value={filters.action}
              onChange={(e) => setFilters({ ...filters, action: e.target.value })}
            >
              <option value="">All</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="status_change">Status Change</option>
              <option value="delete">Delete</option>
            </Form.Control>
          </Form.Group>
          <Form.Group className="col-md-2">
            <Form.Label>From</Form.Label>
            <Form.Control
              type="date"
              value={filters.fromDate}
              onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="col-md-2">
            <Form.Label>To</Form.Label>
            <Form.Control
              type="date"
              value={filters.toDate}
              onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="col-md-2">
            <Form.Label>Per page</Form.Label>
            <Form.Control
              as="select"
              value={String(filters.limit)}
              onChange={(e) => setFilters({ ...filters, limit: Number(e.target.value) || 100 })}
            >
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
            </Form.Control>
          </Form.Group>
          <Form.Group className="col-md-2 d-flex align-items-end">
            <Button variant="primary" onClick={handleSearch} disabled={loading}>
              {loading ? <Spinner as="span" animation="border" size="sm" /> : 'Search'}
            </Button>
          </Form.Group>
        </div>
      </Form>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* Table */}
      <Table responsive size="sm" bordered hover className="mt-2">
        <thead className="thead-light">
          <tr>
            <th style={{ width: 150 }}>Date &amp; Time</th>
            <th style={{ width: 160 }}>User</th>
            <th style={{ width: 100 }}>Action</th>
            <th style={{ width: 90 }}>Entity</th>
            <th>Name</th>
            <th>Summary</th>
            <th style={{ width: 70 }} />
          </tr>
        </thead>
        <tbody>
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={7} className="text-center text-muted py-3">
                No changes recorded for the selected filters.
              </td>
            </tr>
          )}
          {rows.map((r) => (
            <React.Fragment key={r.id}>
              <tr>
                <td>{moment(r.createdAt).format('YYYY-MM-DD HH:mm')}</td>
                <td>{r.changedByName || r.changedBy || '—'}</td>
                <td>{actionBadge(r.action)}</td>
                <td>{r.entityType}</td>
                <td>{r.entityName || <span className="text-muted small">{r.entityId}</span>}</td>
                <td style={{ maxWidth: 500, overflowWrap: 'anywhere' }}>
                  {r.summary || <span className="text-muted">—</span>}
                </td>
                <td>
                  {r.changes && Object.keys(r.changes).length > 0 ? (
                    <Button size="sm" variant="outline-secondary" onClick={() => toggleExpand(r.id)}>
                      {expanded.has(r.id) ? 'Hide' : 'View'}
                    </Button>
                  ) : null}
                </td>
              </tr>
              {expanded.has(r.id) && r.changes && (
                <tr>
                  <td colSpan={7} style={{ background: '#fafbfc' }}>
                    <div className="p-2">
                      <Table size="sm" bordered className="mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: 200 }}>Field</th>
                            <th>Old</th>
                            <th>New</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(r.changes).map(([field, v]) => (
                            <tr key={field}>
                              <td><code>{field}</code></td>
                              <td style={{ overflowWrap: 'anywhere' }}>{formatValue(v.old)}</td>
                              <td style={{ overflowWrap: 'anywhere' }}>{formatValue(v.new)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </Table>

      {/* Pagination */}
      <div className="d-flex align-items-center justify-content-between mt-2">
        <span className="text-muted small">
          {total > 0
            ? `Showing ${filters.offset + 1}-${Math.min(filters.offset + rows.length, total)} of ${total}`
            : '0 results'}
        </span>
        <div>
          <Button size="sm" variant="outline-secondary" className="mr-2" disabled={!hasPrev || loading} onClick={() => handlePage(-1)}>
            Previous
          </Button>
          <Button size="sm" variant="outline-secondary" disabled={!hasNext || loading} onClick={() => handlePage(1)}>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChangeLogPage;
